import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  Portal,
  Snackbar,
  TableCell,
  TableRow,
  Typography,
} from "@material-ui/core";
import { AlertTitle } from "@material-ui/lab";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import { safeDateFormat } from "../utils";
import { navigateToTrustedUrl, openTrustedUrl } from "../urlSafety";
import { Alert } from "features/SnackBar/SnackbarAlert";
import FileImage from "images/fileIcon.svg";
import DotsImage from "images/threeDots.svg";
import React, { useEffect, useRef, useState } from "react";
import { useSnackbar } from "contexts/SnackbarContext";
import { useDispatch } from "react-redux";

import { postActivity } from "slices/activitySlice";
import InfoIcon from "images/InfoIcon.svg";
import EditPermissions from "./EditPermissions";
import { deleteFile, DownloadFile, UpdateFile } from "./FileApi";
import FactsheetEditor from "./FactsheetEditor";
import SendForSign from "./SendforSign";

export default function FileView({
  file,
  disputeId,
  classes,
  reload,
  profile,
  allProfiles,
  parent_folder,
  selectedFiles,
  handleChangeOnFileCheckbox,
  unSelectAll,
  isHidePermissionsFeature = false,
  customDisputeId,
  // Closed case: only Download stays in the menu.
  readOnly = false,
}) {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const hasFilePermission = (targetFile) => {
    const isUploader =
      parseInt(targetFile?.uploaded_by?.id || targetFile?.uploaded_by) === parseInt(profile?.user?.id);
    const isWebnyayOwner =
      targetFile?.webnyay_owner &&
      (parseInt(targetFile?.webnyay_owner?.id || targetFile?.webnyay_owner) === parseInt(profile?.id) ||
        parseInt(targetFile?.webnyay_owner?.id || targetFile?.webnyay_owner) === parseInt(profile?.user?.id));
    const isAllowedUser =
      Array.isArray(targetFile?.webnyay_allowed_users) &&
      targetFile.webnyay_allowed_users.some(
        (u) => parseInt(u?.id || u) === parseInt(profile?.id) || parseInt(u?.id || u) === parseInt(profile?.user?.id)
      );

    return isUploader || isWebnyayOwner || isAllowedUser;
  };

  const [buttonsEnabled, setButtonsEnabled] = useState(() => hasFilePermission(file));
  useEffect(() => {
    setButtonsEnabled(hasFilePermission(file));
  }, [file, profile]);

  const [openmodal, setOpenmodal] = useState(false);
  const handleOpenEdit = () => {
    const isAdminRole =
      profile?.profile_type === "case_manager" ||
      profile?.profile_type === "sub_admin_tiac_rv";

    if (hasFilePermission(file) || isAdminRole) {
      setOpenmodal(true);
    } else showSnackbar("Only the owner can edit permissions.", "warning");
  };
  const handleCloseModalAndRefresh = () => {
    reload();
    setOpenmodal(false);
    setAnchorEl(null);
  };
  const handleCloseModal = () => {
    setOpenmodal(false);
    setAnchorEl(null);
  };

  const [openmodalSignature, setOpenmodalSignature] = useState(false);
  const handleOpenSignature = () => {
    if (
      profile.profile_type == "sub_admin_tiac_rv" ||
      profile.profile_type == "case_manager"
    )
      setOpenmodalSignature(true);
    else showSnackbar("Only an admin or case manager can use this functionality.", "warning");
  };
  const handleCloseSignature = () => {
    setOpenmodalSignature(false);
    setAnchorEl(null);
  };

  const [openmodalEditDoc, setOpenmodalEditDoc] = useState(false);
  const canEditDocument = Boolean(file?.editable) && !readOnly;
  const handleOpenEditDoc = () => {
    handleClose();
    setOpenmodalEditDoc(true);
  };
  const handleCloseEditDoc = () => {
    setOpenmodalEditDoc(false);
  };

  // Extract prefix if it exists (all characters up to the first '__')
  let prefix = "";
  const prefixIndex = file.name.indexOf("__");
  if (prefixIndex !== -1) {
    prefix = file.name.substring(0, prefixIndex + 2);
  }

  // The case number (eg. "AC No. 383/2026_") sits right after the prefix
  const caseNumberPrefix = customDisputeId ? `${customDisputeId}_` : "";
  const fullPrefix =
    caseNumberPrefix && file.name.substring(prefix.length).startsWith(caseNumberPrefix)
      ? prefix + caseNumberPrefix
      : prefix;

  const nameWithoutPrefix = file.name.substring(fullPrefix.length);

  let extension = "";
  const extIndex = nameWithoutPrefix.lastIndexOf(".");
  if (extIndex !== -1) {
    extension = nameWithoutPrefix.substring(extIndex);
  }

  // Extract the clean, editable local name
  const localName = nameWithoutPrefix.substring(0, extIndex !== -1 ? extIndex : nameWithoutPrefix.length);

  const [nameWithoutExtension, setNameWithoutExtension] = useState(localName);

  const [rename, setRename] = useState(false);
  const handleFileNameChange = (e) => {
    setNameWithoutExtension(e.target.value);
  };
  const inputRef = useRef(null);
  const handleOnBlur = (e) => {
    setRename(false);
    if (nameWithoutExtension !== localName) {
      const previousName = file.name;
      const updatedName = fullPrefix + nameWithoutExtension + extension;
      UpdateFile(file.id, {
        name: updatedName,
      }).then(() => {
        dispatch(
          postActivity(
            `renamed a file ${previousName} to ${updatedName}`,
            file.size,
            true,
            updatedName,
            disputeId,
            [],
            file.id
          )
        );
        reload();
      });
    }
  };

  const handleRename = () => {
    if (buttonsEnabled) {
      handleClose();
      setRename(true);
      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.select();
      }, 100);
    } else {
      showSnackbar("You don't have edit permissions.", "warning");
    }
  };

  const handleDelete = () => {
    if (buttonsEnabled) {
      handleClose();
      let p = [];
      // p.push(file?.uploaded_by?.id);
      deleteFile(file.id).then(() => {
        dispatch(
          postActivity(
            "deleted a file",
            file.size,
            true,
            file.name,
            disputeId,
            p
          )
        );
        reload();
        unSelectAll();
      });
    } else {
      showSnackbar("You don't have delete permissions.", "warning");
    }
  };

  const handleDownload = (event) => {
    event.stopPropagation();
    DownloadFile(file.id)
      .then((res) => {
        // The presigned URL comes back from the document-management service, so
        // it is validated before the browser is pointed at it.
        const onError = (message) => showSnackbar(message, "error");
        if (file.name?.toLowerCase().endsWith(".pdf")) {
          openTrustedUrl(res.data.url, { onError, context: "file preview" });
        } else {
          if (navigateToTrustedUrl(res.data.url, { onError, context: "file download" })) {
            handleOpenSnackBarForDownload();
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const [snackBarForDownload, setsnackBarForDownload] = useState(false);
  const handleOpenSnackBarForDownload = () => {
    setsnackBarForDownload(true);
  };
  const handleCloseSnackBarDownload = () => {
    setsnackBarForDownload(false);
  };

  const SnackBarStyles = {
    boxShadow: "none",
    borderRadius: 0,
    textWrap: "wrap",
    color: TEXT.muted,
    border: "1px solid #AAC6ED",
    background: "#E7EFFA",
  };

  const isSelected = selectedFiles?.includes(`${file.id}`);

  const isFirstLevelFile = !parent_folder;

  const canModifyFile = buttonsEnabled && !file?.system_generated && !readOnly;
  const canEditPermissions =
    (hasFilePermission(file) ||
      profile?.profile_type === "case_manager" ||
      profile?.profile_type === "sub_admin_tiac_rv") &&
    !isHidePermissionsFeature &&
    !readOnly;

  const showOptionsMenu = !isFirstLevelFile || canEditDocument;

  return (
    <>
      {/* A row of the Files table; the menu, dialogs and snackbar below render
          outside it, so a double-click inside them can't start a download. */}
      <TableRow
        className={classes.filesHover}
        onDoubleClick={handleDownload}
        data-tip
        data-for="download-guide"
      >
        <TableCell>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              id={file.id}
              color="primary"
              className={
                isSelected ? classes.visibility : classes.visibilityHidden
              }
              style={{ padding: "5px", height: "fit-content", flexShrink: 0 }}
              checked={isSelected}
              onChange={handleChangeOnFileCheckbox}
            />
            <span
              style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <img src={FileImage} alt="file" />
            </span>
            <Typography
              className={`${classes.tableRowContent} ${classes.no_selection}`}
              style={{ marginLeft: "1rem", minWidth: 0 }}
            >
              {!rename ? (
                <span
                  className={classes.fileFolderName}
                  onClick={handleDownload}
                >
                  {file.name}
                </span>
              ) : (
                <input
                  ref={inputRef}
                  value={nameWithoutExtension}
                  style={{ width: "70%" }}
                  onChange={handleFileNameChange}
                  onBlur={handleOnBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleOnBlur();
                    }
                  }}
                />
              )}
            </Typography>
          </div>
        </TableCell>
        <TableCell>
          <Typography
            className={`${classes.tableRowContent} ${classes.no_selection}`}
          >
            {file.owner_name}
          </Typography>
        </TableCell>
        {profile.profile_type === "case_manager" && (
          <TableCell>
            <Typography
              className={`${classes.tableRowContent} ${classes.no_selection}`}
            >
              {safeDateFormat(file.modified, "dd/MM/yyyy, HH:mm")}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Typography
            className={`${classes.tableRowContent} ${classes.no_selection}`}
          >
            {safeDateFormat(file.created, "dd/MM/yyyy, HH:mm")}
            {showOptionsMenu && (
              <IconButton
                onClick={handleClick}
                aria-controls={`three-dots-menu${file.id}`}
                aria-haspopup="true"
                style={{ padding: 0, float: "right" }}
              >
                <img src={DotsImage} alt="options" />
              </IconButton>
            )}
          </Typography>
        </TableCell>
      </TableRow>
      {/* <CustomReactTooltip id='download-guide'>
        <Typography style={{color:'white',lineHeight:'14px',fontSize:'0.8rem'}}>
          Double click to download
        </Typography>
      </CustomReactTooltip> */}
      <Menu
        aria-controls={`three-dots-menu${file.id}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        style={{ padding: 0 }}
      >
        <Box className={classes.menuwidth}>
          <Box>
            {canEditDocument && (
              <Typography
                className={classes.filesandfolder}
                onClick={handleOpenEditDoc}
              >
                Edit
              </Typography>
            )}
            {canModifyFile && (
              <>
                <Typography
                  className={classes.filesandfolder}
                  onClick={handleRename}
                >
                  Rename
                </Typography>
                <Typography
                  className={classes.filesandfolder}
                  onClick={handleDelete}
                >
                  Delete
                </Typography>
              </>
            )}
            {canEditPermissions && (
              <Typography
                className={classes.filesandfolder}
                onClick={handleOpenEdit}
              >
                Edit permissions
              </Typography>
            )}
            {/* {(profile.profile_type == "sub_admin_tiac_rv" ||
              profile.profile_type == "case_manager") && (
              <Typography
                className={classes.filesandfolder}
                onClick={handleOpenSignature}
              >
                Send for Sign
              </Typography>
            )} */}
            <Typography
              className={classes.filesandfolder}
              onClick={handleDownload}
            >
              Download
            </Typography>
          </Box>
        </Box>
      </Menu>
      {openmodal && (
        <EditPermissions
          openmodal={openmodal}
          handleCloseModal={handleCloseModal}
          handleCloseModalRefresh={handleCloseModalAndRefresh}
          files={[file]}
          folders={[]}
          allProfiles={allProfiles}
          parent_folder={parent_folder}
          disputeId={disputeId}
          profile={profile}
          viewPermission={file.view_permission}
          editPermission={file.permission}
        />
      )}
      {openmodalEditDoc && (
        <FactsheetEditor
          open={openmodalEditDoc}
          onClose={handleCloseEditDoc}
          fileId={file.id}
          fileName={file.name}
          disputeId={disputeId}
          caseNumber={customDisputeId}
          // onSaved={reload}
        />
      )}
      <SendForSign
        openmodal={openmodalSignature}
        handleCloseModal={handleCloseSignature}
        file={file}
        allProfiles={allProfiles}
        parent_folder={parent_folder}
        disputeId={disputeId}
      />
      {/* Snackbar renders in place, unlike the Menu and Modals; a portal keeps
          its <div> out of the table body. */}
      <Portal>
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          autoHideDuration={3000}
          open={snackBarForDownload}
          onClose={handleCloseSnackBarDownload}
        >
          <Alert
            onClose={handleCloseSnackBarDownload}
            severity="info"
            style={SnackBarStyles}
            iconMapping={{
              info: (
                <img
                  src={InfoIcon}
                  alt="info"
                  style={{ height: "48px", width: "48px" }}
                />
              ),
            }}
          >
            <AlertTitle style={{ color: "black", fontWeigt: 700 }}>
              Information
            </AlertTitle>
            Download is in progress...
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
}
